import { useEffect, useState } from 'react';
import { get, post, put, del, errorMessage } from '../api';
import { navigate } from '../router';
import { Field, Modal, Spinner } from '../ui/controls';
import { toast } from '../ui/toast';
import { thumbUrl } from '../assets';
import { DuplicateDialog, type Customer, type ProjectRow } from './Dashboard';

interface Brand { id: string; name: string; customer_id: string | null; data: { logoAssetId: string | null; contact: string } }

export function Customers() {
  const [q, setQ] = useState('');
  const [list, setList] = useState<Customer[] | null>(null);
  const [sel, setSel] = useState<Customer | null>(null);
  const [editing, setEditing] = useState<Partial<Customer> | null>(null);
  const load = () => get<{ customers: Customer[] }>(`/api/customers?q=${encodeURIComponent(q)}`).then((r) => setList(r.customers)).catch((e) => toast(errorMessage(e), 'err'));
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="page-scroll"><div className="page col" style={{ gap: 16 }}>
      <div className="row"><h1 className="grow">Customers</h1><button className="btn primary" onClick={() => setEditing({ name: '', email: '', phone: '', notes: '' })}>+ New customer</button></div>
      <div className="row" style={{ alignItems: 'flex-start', gap: 16 }}>
        <div className="card" style={{ width: 320, flex: 'none' }}>
          <div className="section"><input className="input" type="search" placeholder="Search customers…" aria-label="Search customers" value={q} onChange={(e) => setQ(e.target.value)} /></div>
          {!list && <div className="section"><Spinner /></div>}
          {list?.length === 0 && <div className="empty small">{q ? 'No matches.' : 'No customers yet.'}</div>}
          <div className="layers" style={{ padding: 8 }}>
            {list?.map((c) => (
              <button key={c.id} aria-pressed={sel?.id === c.id} onClick={() => setSel(c)}>
                <span className="grow">{c.name}</span><span className="badge">{c.projectCount ?? 0}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="grow">{sel ? <CustomerDetail c={sel} onEdit={() => setEditing(sel)} /> : <div className="card empty">Select a customer to see their calendars and brand presets.</div>}</div>
      </div>
      {editing && (
        <Modal title={editing.id ? 'Edit customer' : 'New customer'} onClose={() => setEditing(null)} footer={<><button className="btn" onClick={() => setEditing(null)}>Cancel</button><button className="btn primary" disabled={!editing.name?.trim()} onClick={async () => {
          try {
            if (editing.id) await put(`/api/customers/${editing.id}`, editing); else { const r = await post<{ customer: Customer }>('/api/customers', editing); setSel(r.customer); }
            setEditing(null); void load();
          } catch (e) { toast(errorMessage(e), 'err'); }
        }}>Save</button></>}>
          {(['name', 'email', 'phone'] as const).map((k) => <Field key={k} label={k[0].toUpperCase() + k.slice(1)}><input className="input" value={editing[k] ?? ''} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} /></Field>)}
          <Field label="Notes"><textarea className="input" rows={3} value={editing.notes ?? ''} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></Field>
        </Modal>
      )}
    </div></div>
  );
}

function CustomerDetail({ c, onEdit }: { c: Customer; onEdit: () => void }) {
  const [projects, setProjects] = useState<ProjectRow[] | null>(null);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [dup, setDup] = useState<ProjectRow | null>(null);
  useEffect(() => {
    setProjects(null);
    get<{ projects: ProjectRow[] }>(`/api/projects?customerId=${c.id}`).then((r) => setProjects(r.projects));
    get<{ brands: Brand[] }>(`/api/brands?customerId=${c.id}`).then((r) => setBrands(r.brands));
  }, [c.id]);
  return (
    <div className="col" style={{ gap: 16 }}>
      <div className="card pad row">
        <div className="grow"><h2 style={{ margin: 0 }}>{c.name}</h2><span className="small muted">{[c.email, c.phone].filter(Boolean).join(' · ') || 'No contact details'}</span>{c.notes && <p className="small" style={{ marginTop: 6 }}>{c.notes}</p>}</div>
        <button className="btn sm" onClick={onEdit}>Edit</button>
      </div>
      <div className="card pad col">
        <h3>Calendars</h3>
        {!projects && <Spinner />}
        {projects?.length === 0 && <p className="small faint">No calendars for this customer yet.</p>}
        <table className="tbl"><tbody>
          {projects?.map((p) => (
            <tr key={p.id}>
              <td style={{ width: 56 }}><div style={{ width: 48, height: 36, borderRadius: 4, background: p.thumbnailAssetId ? `#eee url(${thumbUrl(p.thumbnailAssetId)}) center/cover` : '#eee' }} /></td>
              <td><strong>{p.name}</strong><div className="small faint">{p.year} · {p.status.replace('_', ' ')} · rev {p.version}</div></td>
              <td style={{ textAlign: 'right' }}><button className="btn sm" onClick={() => navigate(`/projects/${p.id}`)}>Open</button> <button className="btn sm" onClick={() => setDup(p)}>Duplicate…</button></td>
            </tr>
          ))}
        </tbody></table>
      </div>
      <div className="card pad col">
        <h3>Brand presets</h3>
        {brands.length === 0 && <p className="small faint">None yet. Create one from a project: Design → Branding → “Save as brand preset”.</p>}
        {brands.map((b) => (
          <div key={b.id} className="row">
            {b.data.logoAssetId && <img src={thumbUrl(b.data.logoAssetId)} alt="" style={{ height: 28, maxWidth: 80, objectFit: 'contain' }} />}
            <span className="grow"><strong>{b.name}</strong> <span className="small faint">{b.data.contact.slice(0, 60)}</span></span>
            <button className="btn sm ghost danger" onClick={async () => { if (confirm(`Delete brand preset “${b.name}”?`)) { await del(`/api/brands/${b.id}`); setBrands(brands.filter((x) => x.id !== b.id)); } }}>Delete</button>
          </div>
        ))}
      </div>
      {dup && <DuplicateDialog p={dup} onClose={() => setDup(null)} />}
    </div>
  );
}
